import { getAppLanguage, setAppLanguage } from '@/core/native/storage'

export * from './init'
import { Device } from '@capacitor/device'
import i18n from 'i18next'
import { useEffect, useState } from 'react'
import { useTranslation as useI18NextTranslation } from 'react-i18next'

import { config } from '@/config'
import { SupportedLanguage } from '@/types/config'

import { dayjs } from '../../lib/dayjs'

type UseI18NextParams = Parameters<typeof useI18NextTranslation>

export function useTranslation(...props: UseI18NextParams) {
  const res = useI18NextTranslation(...props)

  return {
    ...res,
    format(data: Date) {
      dayjs(data).fromNow()
    },
  }
}

/**
 * @description
 * Switch the app language by referring to the language setting of localStorage or device browser
 */
export function useLocalLanguage() {
  const [translated, setTranslated] = useState(false)
  useEffect(() => {
    if (translated) return

    const changeLanguage = async () => {
      const languageStorage = await getAppLanguage()
      const deviceLanguageCode = await Device.getLanguageCode()

      const languageCode = getLongLanguageCode(
        languageStorage ?? deviceLanguageCode.value
      )
      const [shortLanguageCode] = languageCode.split('-')

      // i18nの言語情報をdayJSに設定
      dayjs.locale(shortLanguageCode)
      await i18n.changeLanguage(shortLanguageCode)
      await setAppLanguage(languageCode)
    }
    changeLanguage()
    setTranslated(true)
  }, [translated])
}

/**
 * return long language code
 * @param language
 *
 * @example
 * getLongLanguageCode('ja-JP') // return 'ja-JP'
 * getLongLanguageCode('ja') // return 'ja-JP'
 */
export function getLongLanguageCode(
  language: SupportedLanguage | string
): SupportedLanguage {
  const languageCodes: Record<SupportedLanguage | string, SupportedLanguage> = {
    en: 'en-US',
    'en-US': 'en-US',
    ja: 'ja-JP',
    'ja-JP': 'ja-JP',
  }
  return languageCodes[language] ?? 'ja-JP'
}

type UseCustomTranslationResult = {
  t: (key: string, options?: Record<string, any>) => string
  i18n: typeof i18n
  ready: boolean
}

/**
 * @description
 * 指定された namespace のカスタム翻訳を読み込み、t 関数を返す
 */
export function useCustomTranslation(
  namespace: string
): UseCustomTranslationResult {
  const { i18n: i18nInstance } = useTranslation()
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const loadCustomTranslation = async () => {
      const lang = i18nInstance.language || 'ja'
      const url = `${config.commonStorageUrl}customDesign/locales/${lang}/${namespace}.json`

      try {
        const res = await fetch(url)
        if (!res.ok) throw new Error('Failed to fetch translation JSON')

        const json = await res.json()

        i18n.addResourceBundle(lang, namespace, json, true, true)
        setLoaded(true)
      } catch (e) {
        console.error(`Error loading ${namespace} for ${lang}`, e)
      }
    }

    if (!i18n.hasResourceBundle(i18nInstance.language, namespace)) {
      loadCustomTranslation()
    } else {
      setLoaded(true)
    }
  }, [i18nInstance.language, namespace])

  const t = (key: string, options?: Record<string, any>): string => {
    const result = i18n.t(`${namespace}:${key}`, options)
    return typeof result === 'string' ? result : String(result)
  }

  return { t, i18n, ready: loaded }
}

/**
 * @description
 * 指定された namespace のカスタム翻訳を読み込み、t 関数を返す
 */
export function useArtistCustomTranslation(
  artistId: string,
  namespace: string
): UseCustomTranslationResult {
  const { i18n: i18nInstance } = useTranslation()
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const loadCustomTranslation = async () => {
      const lang = i18nInstance.language || 'ja'
      const url = `${config.appStorageUrl}customDesign/artists/${artistId}/locales/${lang}/${namespace}.json`

      try {
        const res = await fetch(url)
        if (!res.ok) throw new Error('Failed to fetch translation JSON')

        const json = await res.json()

        i18n.addResourceBundle(lang, namespace, json, true, true)
        setLoaded(true)
      } catch (e) {
        console.error(`Error loading ${namespace} for ${lang}`, e)
      }
    }

    if (!i18n.hasResourceBundle(i18nInstance.language, namespace)) {
      loadCustomTranslation()
    } else {
      setLoaded(true)
    }
  }, [i18nInstance.language, namespace])

  const t = (key: string, options?: Record<string, any>): string => {
    const result = i18n.t(`${namespace}:${key}`, options)
    return typeof result === 'string' ? result : String(result)
  }

  return { t, i18n, ready: loaded }
}
